import { Add, ArrowForwardIos, Delete } from '@mui/icons-material';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Paper, Select, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import apiClient from '../api/client';

const SectionScripts = ({ sectionId }) => {
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [schema, setSchema] = useState([]);
  const initialForm = { conditionField: '', operator: '==', conditionValue: '', actionField: '', actionType: 'assign', actionValue: '' };
  const [form, setForm] = useState(initialForm);

  useEffect(() => {
    if (!sectionId) return;
    // Получаем схему раздела
    const fetchSection = async () => {
      try {
        const resp = await apiClient.get(`/sections/${sectionId}`);
        setSchema(resp.data.schema || []);
      } catch (err) {
        console.error('Ошибка при загрузке схемы раздела:', err);
      }
    };
    fetchSection();
    fetchScripts();
  }, [sectionId]);

  const fetchScripts = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/sections/${sectionId}/scripts`);
      setScripts(response.data);
      setError('');
    } catch (err) {
      console.error('Ошибка при загрузке скриптов:', err);
      setError('Не удалось загрузить скрипты');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setForm(initialForm);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Построение условного выражения
      const condType = schema.find(f => f.name === form.conditionField)?.type;
      const condValLiteral = condType === 'number' && !isNaN(form.conditionValue)
        ? form.conditionValue
        : `'${form.conditionValue}'`;
      let conditionStr;
      if (form.operator === 'contains') {
        conditionStr = `data['${form.conditionField}'].includes(${condValLiteral})`;
      } else {
        conditionStr = `data['${form.conditionField}'] ${form.operator} ${condValLiteral}`;
      }
      // Построение действия
      const actType = schema.find(f => f.name === form.actionField)?.type;
      const actionValLiteral = actType === 'number' && !isNaN(form.actionValue)
        ? form.actionValue
        : `'${form.actionValue}'`;
      let actionStr = '';
      switch (form.actionType) {
        case 'assign': actionStr = `data['${form.actionField}'] = ${actionValLiteral}`; break;
        case 'increment': actionStr = `data['${form.actionField}'] += ${actionValLiteral}`; break;
        case 'decrement': actionStr = `data['${form.actionField}'] -= ${actionValLiteral}`; break;
        case 'multiply': actionStr = `data['${form.actionField}'] *= ${actionValLiteral}`; break;
        case 'divide': actionStr = `data['${form.actionField}'] /= ${actionValLiteral}`; break;
        default: break;
      }
      await apiClient.post(`/sections/${sectionId}/scripts`, { condition: conditionStr, action: actionStr });
      handleClose();
      fetchScripts();
    } catch (err) {
      console.error('Ошибка при сохранении скрипта:', err);
      setError('Не удалось сохранить скрипт');
    }
  };

  const handleDelete = async (index) => {
    if (window.confirm('Удалить этот скрипт?')) {
      try {
        await apiClient.delete(`/sections/${sectionId}/scripts/${index}`);
        fetchScripts();
      } catch (err) {
        console.error('Ошибка при удалении скрипта:', err);
        setError('Не удалось удалить скрипт');
      }
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Скрипты раздела</Typography>
        <Button variant="contained" color="success" startIcon={<Add />} onClick={handleAdd}>Добавить скрипт</Button>
      </Box>
      {error && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>{error}</Paper>
      )}
      <Box>
        {loading ? (
          <Typography>Загрузка...</Typography>
        ) : scripts.length === 0 ? (
          <Typography>Скрипты не найдены</Typography>
        ) : (
          scripts.map((script, idx) => (
            <Paper key={idx} sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                <Paper sx={{ p: 1, background: '#f0f0f0' }}><Typography>{script.condition}</Typography></Paper>
                <ArrowForwardIos />
                <Paper sx={{ p: 1, background: '#f0f0f0' }}><Typography>{script.action}</Typography></Paper>
              </Box>
              <IconButton color="error" onClick={() => handleDelete(idx)}><Delete /></IconButton>
            </Paper>
          ))
        )}
      </Box>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Новый скрипт</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Typography variant="subtitle1" sx={{ mt: 1 }}>Условие:</Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Поле</InputLabel>
                <Select name="conditionField" value={form.conditionField} label="Поле" onChange={handleChange} required>
                  {schema.map(f => <MenuItem key={f.name} value={f.name}>{f.name}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl>
                <InputLabel>Оператор</InputLabel>
                <Select name="operator" value={form.operator} label="Оператор" onChange={handleChange} required>
                  <MenuItem value="==">=</MenuItem>
                  <MenuItem value="!=">≠</MenuItem>
                  <MenuItem value=">">&gt;</MenuItem>
                  <MenuItem value=">=">≥</MenuItem>
                  <MenuItem value="<">&lt;</MenuItem>
                  <MenuItem value="<=">≤</MenuItem>
                  <MenuItem value="contains">содержит</MenuItem>
                </Select>
              </FormControl>
              <TextField label="Значение" name="conditionValue" value={form.conditionValue} onChange={handleChange} required />
            </Box>
            <Typography variant="subtitle1" sx={{ mt: 1 }}>Действие:</Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Поле</InputLabel>
                <Select name="actionField" value={form.actionField} label="Поле" onChange={handleChange} required>
                  {schema.map(f => <MenuItem key={f.name} value={f.name}>{f.name}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl>
                <InputLabel>Действие</InputLabel>
                <Select name="actionType" value={form.actionType} label="Действие" onChange={handleChange} required>
                  <MenuItem value="assign">Установить</MenuItem>
                  <MenuItem value="increment">Увеличить</MenuItem>
                  <MenuItem value="decrement">Уменьшить</MenuItem>
                  <MenuItem value="multiply">Умножить</MenuItem>
                  <MenuItem value="divide">Разделить</MenuItem>
                </Select>
              </FormControl>
              <TextField label="Значение" name="actionValue" value={form.actionValue} onChange={handleChange} required />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Отмена</Button>
            <Button type="submit" variant="contained">Сохранить</Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default SectionScripts; 